import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { QT_DAILY_COCKPIT_URL, customerEvidenceQuestion, qaHref } from '../contextLinks';

type LifecycleStage = 'lead' | 'qualified' | 'customer' | 'past_customer' | 'dead';

interface Contact {
  id: number;
  display_name: string;
  primary_phone: string | null;
  primary_email: string | null;
  company: string | null;
  hubspot_id: string | null;
  odoo_partner_id: number | null;
  lifecycle_stage: LifecycleStage | null;
  contact_properties: Record<string, unknown>;
  is_active: boolean;
  soft_deleted_at: string | null;
  soft_deleted_by: string | null;
  created_source: string | null;
  last_updated_source: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

interface TimelineRow {
  kind: 'call' | 'sms' | 'form' | 'manual_engagement' | 'action_item' | 'deal';
  id: string | number;
  title: string;
  date: string | null;
}

interface PreviewBundle {
  calls: Array<Record<string, unknown>>;
  sms: Array<Record<string, unknown>>;
  forms: Array<Record<string, unknown>>;
  manual_engagements: Array<Record<string, unknown>>;
  action_items: Array<Record<string, unknown>>;
  deals: Array<Record<string, unknown>>;
}

interface DetailResponse {
  contact: Contact;
  preview: PreviewBundle;
}

const LIFECYCLE_VALUES: LifecycleStage[] = ['lead', 'qualified', 'customer', 'past_customer', 'dead'];

const LIFECYCLE_CHIP: Record<LifecycleStage, string> = {
  lead: 'bg-blue-50 text-blue-900 ring-blue-200',
  qualified: 'bg-indigo-50 text-indigo-900 ring-indigo-200',
  customer: 'bg-green-50 text-green-900 ring-green-200',
  past_customer: 'bg-slate-100 text-slate-700 ring-slate-200',
  dead: 'bg-rose-50 text-rose-900 ring-rose-200',
};

const KIND_CHIP: Record<TimelineRow['kind'], string> = {
  call: 'bg-amber-50 text-amber-900 ring-amber-200',
  sms: 'bg-sky-50 text-sky-900 ring-sky-200',
  form: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
  manual_engagement: 'bg-fuchsia-50 text-fuchsia-900 ring-fuchsia-200',
  action_item: 'bg-rose-50 text-rose-900 ring-rose-200',
  deal: 'bg-violet-50 text-violet-900 ring-violet-200',
};

const KIND_LABEL: Record<TimelineRow['kind'], string> = {
  call: 'Call',
  sms: 'SMS',
  form: 'Form',
  manual_engagement: 'Engagement',
  action_item: 'Action item',
  deal: 'Deal',
};

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

function pickAny(obj: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return null;
}

function dateOf(obj: Record<string, unknown>): string | null {
  const candidates = ['occurred_at', 'sent_at', 'submitted_at', 'created_at', 'due_at', 'closed_at', 'updated_at', 'date'];
  return pickString(obj, candidates);
}

function titleOf(obj: Record<string, unknown>, fallback: string): string {
  const t = pickString(obj, ['title', 'subject', 'name', 'summary', 'form_name', 'call_name', 'message', 'body']);
  return t ?? fallback;
}

function idOf(obj: Record<string, unknown>, fallback: string | number): string | number {
  const v = pickAny(obj, ['id', 'call_id', 'sms_id', 'form_id', 'engagement_id', 'action_item_id', 'deal_id']);
  if (typeof v === 'string' || typeof v === 'number') return v;
  return fallback;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function LifecycleChip({ stage }: { stage: LifecycleStage | null }): JSX.Element {
  if (!stage) {
    return <span className="text-xs text-slate-400">no stage</span>;
  }
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${LIFECYCLE_CHIP[stage]}`}>
      {stage.replace('_', ' ')}
    </span>
  );
}

export function ContactDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Property editor local state
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  async function fetchDetail(): Promise<void> {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/contacts/${id}`, { credentials: 'include' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? `Request failed: ${res.status}`);
        setLoading(false);
        return;
      }
      const payload = (await res.json()) as DetailResponse;
      setData(payload);
      setLoading(false);
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function patchContact(body: Record<string, unknown>): Promise<void> {
    if (!id) return;
    setActionPending(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/contacts/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errBody.error ?? `Request failed: ${res.status}`);
        setActionPending(false);
        return;
      }
      const payload = (await res.json()) as { contact: Contact };
      setData((prev) => (prev ? { ...prev, contact: payload.contact } : prev));
      setActionPending(false);
    } catch (err) {
      setActionError((err as Error).message);
      setActionPending(false);
    }
  }

  async function softDelete(): Promise<void> {
    if (!id) return;
    if (!confirm('Soft-delete this contact?')) return;
    setActionPending(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/contacts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.status !== 204) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errBody.error ?? `Request failed: ${res.status}`);
        setActionPending(false);
        return;
      }
      setActionPending(false);
      await fetchDetail();
    } catch (err) {
      setActionError((err as Error).message);
      setActionPending(false);
    }
  }

  async function restore(): Promise<void> {
    if (!id) return;
    setActionPending(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/contacts/${id}/restore`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errBody.error ?? `Request failed: ${res.status}`);
        setActionPending(false);
        return;
      }
      const payload = (await res.json()) as { contact: Contact };
      setData((prev) => (prev ? { ...prev, contact: payload.contact } : prev));
      setActionPending(false);
    } catch (err) {
      setActionError((err as Error).message);
      setActionPending(false);
    }
  }

  const timeline = useMemo<TimelineRow[]>(() => {
    if (!data) return [];
    const rows: TimelineRow[] = [];
    for (const c of data.preview.calls) {
      rows.push({ kind: 'call', id: idOf(c, `call-${rows.length}`), title: titleOf(c, 'Call'), date: dateOf(c) });
    }
    for (const s of data.preview.sms) {
      rows.push({ kind: 'sms', id: idOf(s, `sms-${rows.length}`), title: titleOf(s, 'SMS'), date: dateOf(s) });
    }
    for (const f of data.preview.forms) {
      rows.push({ kind: 'form', id: idOf(f, `form-${rows.length}`), title: titleOf(f, 'Form submission'), date: dateOf(f) });
    }
    for (const e of data.preview.manual_engagements) {
      rows.push({ kind: 'manual_engagement', id: idOf(e, `eng-${rows.length}`), title: titleOf(e, 'Engagement'), date: dateOf(e) });
    }
    for (const a of data.preview.action_items) {
      rows.push({ kind: 'action_item', id: idOf(a, `ai-${rows.length}`), title: titleOf(a, 'Action item'), date: dateOf(a) });
    }
    for (const d of data.preview.deals) {
      rows.push({ kind: 'deal', id: idOf(d, `deal-${rows.length}`), title: titleOf(d, 'Deal'), date: dateOf(d) });
    }
    rows.sort((a, b) => {
      const aT = a.date ? new Date(a.date).getTime() : 0;
      const bT = b.date ? new Date(b.date).getTime() : 0;
      return bT - aT;
    });
    return rows;
  }, [data]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white ring-1 ring-slate-200 p-8 text-sm text-slate-500">Loading…</div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <Link to="/contacts" className="text-sm text-slate-600 hover:underline">← Back to contacts</Link>
        <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-5">
          <p className="text-sm font-medium text-rose-900">Failed to load contact</p>
          <p className="mt-1 text-sm text-rose-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return <div />;
  const c = data.contact;
  const props = c.contact_properties ?? {};
  const propKeys = Object.keys(props);

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600 ring-1 ring-slate-200">
        <span className="font-medium text-slate-700">Evidence identity.</span> The customer master of
        record lives in <span className="font-medium text-slate-700">Odoo</span>; edits here repair the
        Sales-QA evidence record — the identity that joins calls, SMS, and engagements to a person — not
        the commercial record. Work the customer in Customer Workbench.
      </div>
      <div className="flex items-center justify-between gap-3">
        <Link to="/contacts" className="text-sm text-slate-600 hover:underline">← Back to contacts</Link>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to={`/contacts/${c.id}/edit`}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
          >
            Edit fields
          </Link>
          <Link
            to={qaHref(customerEvidenceQuestion({
              name: c.display_name,
              company: c.company,
              phone: c.primary_phone,
              email: c.primary_email,
              contactId: c.id,
            }))}
            className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-800 hover:bg-blue-100"
          >
            Ask evidence
          </Link>
          <Link
            to={`/contacts/${c.id}/preflight`}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
          >
            Project Brief Preflight
          </Link>
          <a
            href={QT_DAILY_COCKPIT_URL}
            target="_blank"
            rel="noreferrer"
            title="Leaving Sales-QA evidence. Customer Workbench owns the next action — reply, call, scope, quote, close the loop."
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
          >
            Open Workbench
          </a>
          <button
            type="button"
            onClick={() => navigate(`/engagements?contact_id=${c.id}&new=1`)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
          >
            + Log engagement
          </button>
          {c.is_active ? (
            <button
              type="button"
              disabled={actionPending}
              onClick={() => void softDelete()}
              className="rounded-md bg-rose-600 px-3 py-1 text-sm font-medium text-white hover:bg-rose-700 disabled:bg-rose-300"
            >
              Soft-delete
            </button>
          ) : (
            <button
              type="button"
              disabled={actionPending}
              onClick={() => void restore()}
              className="rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
            >
              Restore
            </button>
          )}
        </div>
      </div>

      <p className="text-xs leading-5 text-slate-500">
        This is the evidence record. To reply, call, scope, quote, or move work, open the customer in{' '}
        <a
          href={QT_DAILY_COCKPIT_URL}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-slate-700 underline hover:text-slate-900"
        >
          Customer Workbench
        </a>
        . Verify quote, order, and invoice truth in Odoo.
      </p>

      {actionError && (
        <section className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-4">
          <p className="text-sm text-rose-900">{actionError}</p>
        </section>
      )}

      <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 truncate">{c.display_name}</h1>
            {c.company && <p className="mt-1 text-sm text-slate-600">{c.company}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <LifecycleChip stage={c.lifecycle_stage} />
              {c.is_active ? (
                <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800 ring-1 ring-green-200">
                  active
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                  inactive
                </span>
              )}
            </div>
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Phone</dt>
                <dd className="text-slate-800">{c.primary_phone ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Email</dt>
                <dd className="text-slate-800">{c.primary_email ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">HubSpot ID</dt>
                <dd className="text-slate-800">{c.hubspot_id ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">
                  Odoo partner ID
                  <span className="ml-1.5 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-slate-600 ring-1 ring-slate-200">
                    customer master
                  </span>
                </dt>
                <dd className="text-slate-800">{c.odoo_partner_id ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">First seen</dt>
                <dd className="text-slate-800" title={c.first_seen_at}>{formatDate(c.first_seen_at)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Last seen</dt>
                <dd className="text-slate-800" title={c.last_seen_at}>{formatDate(c.last_seen_at)}</dd>
              </div>
            </dl>
          </div>
          <div className="shrink-0">
            <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Lifecycle</label>
            <select
              value={c.lifecycle_stage ?? ''}
              disabled={actionPending}
              onChange={(e) => {
                const v = e.target.value;
                void patchContact({ lifecycle_stage: v === '' ? null : v });
              }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            >
              <option value="">— none —</option>
              {LIFECYCLE_VALUES.map((s) => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Properties</h2>
          <span className="text-xs text-slate-400">{propKeys.length} key{propKeys.length === 1 ? '' : 's'}</span>
        </div>
        {propKeys.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No custom properties.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {propKeys.map((k) => (
              <PropertyRow
                key={k}
                propKey={k}
                value={props[k]}
                disabled={actionPending}
                onSave={(v) => patchContact({ contact_properties: { [k]: v } })}
                onDelete={() => patchContact({ contact_properties: { [k]: null } })}
              />
            ))}
          </ul>
        )}
        <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
          <div className="flex-1 min-w-[10rem]">
            <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Key</label>
            <input
              type="text"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="e.g. van_make"
              className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div className="flex-[2] min-w-[12rem]">
            <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Value</label>
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="e.g. Sprinter"
              className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <button
            type="button"
            disabled={actionPending || newKey.trim().length === 0}
            onClick={async () => {
              await patchContact({ contact_properties: { [newKey.trim()]: newValue } });
              setNewKey('');
              setNewValue('');
            }}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
          >
            + Add property
          </button>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Timeline</h2>
        {timeline.length === 0 ? (
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5 text-sm text-slate-500">
            No activity yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {timeline.map((row, i) => (
              <li key={`${row.kind}-${row.id}-${i}`} className="rounded-lg bg-white ring-1 ring-slate-200 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ${KIND_CHIP[row.kind]}`}>
                      {KIND_LABEL[row.kind]}
                    </span>
                    <p className="text-sm text-slate-800 truncate">{row.title}</p>
                  </div>
                  <p className="text-xs text-slate-500 shrink-0" title={row.date ?? undefined}>
                    {formatDate(row.date)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PropertyRow({
  propKey,
  value,
  disabled,
  onSave,
  onDelete,
}: {
  propKey: string;
  value: unknown;
  disabled: boolean;
  onSave: (v: string) => void | Promise<void>;
  onDelete: () => void | Promise<void>;
}): JSX.Element {
  const initial = value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value);
  const [draft, setDraft] = useState(initial);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = draft !== initial;

  return (
    <li className="py-2 flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-slate-700 min-w-[8rem] truncate">{propKey}</span>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="flex-1 min-w-[12rem] rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
      />
      <button
        type="button"
        disabled={disabled || !dirty}
        onClick={() => void onSave(draft)}
        className="rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800 disabled:bg-slate-300"
      >
        Save
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (confirm(`Delete property "${propKey}"?`)) void onDelete();
        }}
        className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
      >
        Delete
      </button>
    </li>
  );
}
